import React, { useState, useEffect, useCallback } from "react";
import CoachReportingSkeleton from "../../components/layout/CoachReportingSkeleton";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { formatQuarterLabel, formatQuarterShort } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import CoachUpgradeGate from "./CoachUpgradeGate";
import * as XLSX from "xlsx";
import {
    BarChart3, MessageSquare, Star, Trash2, Filter,
    Sparkles, Users, PieChart,
    Target, TrendingUp, Flag, Send, UserCheck,
    Download, ChevronRight
} from "lucide-react";

export default function CoachReporting() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [allFeedback, setAllFeedback] = useState([]);
    const [quarters, setQuarters] = useState([]);
    const [selectedQuarter, setSelectedQuarter] = useState("");
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [scorecard, setScorecard] = useState(null);
    const [upgradeRequired, setUpgradeRequired] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        overview: true, students: true, quarterly: true, feedback: true,
    });

    useEffect(() => {
        if (!user?.isCoach && !user?.isAdmin) navigate("/userdashboard");
    }, [user, navigate]);

    const fetchReport = useCallback(async () => {
        try {
            const res = await api.get("/coach/reporting");
            setReport(res.data);
        } catch (err) {
            if (err.response?.status === 403 && err.response?.data?.code === "COACH_UPGRADE_REQUIRED") {
                setUpgradeRequired(true);
            }
            setReport(null);
        }
    }, []);

    const fetchAllFeedback = useCallback(async () => {
        try { const res = await api.get("/coach/feedback/all"); setAllFeedback(res.data.feedback || []); } catch { setAllFeedback([]); }
    }, []);

    const fetchQuarters = useCallback(async () => {
        try { const res = await api.get("/coach/quarters"); setQuarters(res.data.quarters || []); } catch { setQuarters([]); }
    }, []);

    const fetchScorecard = useCallback(async () => {
        try { const res = await api.get("/coach/scorecard"); setScorecard(res.data || null); } catch { setScorecard(null); }
    }, []);

    useEffect(() => {
        const load = async () => { setLoading(true); await Promise.all([fetchReport(), fetchAllFeedback(), fetchQuarters(), fetchScorecard()]); setLoading(false); };
        load();
    }, [fetchReport, fetchAllFeedback, fetchQuarters, fetchScorecard]);

    const showMsg = (text, type = "success") => { setMessage({ text, type }); setTimeout(() => setMessage(null), 4000); };

    const deleteFeedback = async (feedbackId) => {
        setActionLoading(true);
        try {
            await api.delete(`/coach/feedback/${feedbackId}`);
            showMsg("Feedback deleted");
            fetchAllFeedback();
            fetchReport();
        } catch (err) { showMsg(err.response?.data?.error || "Failed to delete", "error"); }
        finally { setActionLoading(false); }
    };

    const getQuarterDates = () => {
        if (!selectedQuarter) return null;
        const q = quarters.find(q => String(q.QuarterID) === String(selectedQuarter));
        return q ? { start: q.QuarterStartDate, end: q.QuarterEndDate } : null;
    };
    const filterFeedbackByQuarter = (items) => {
        const qd = getQuarterDates();
        if (!qd) return items;
        return items.filter(f => { const d = f.CreatedOn?.substring(0, 10); return d && d >= qd.start && d <= qd.end; });
    };

    const avatarColors = ["from-blue-500 to-indigo-600", "from-primary-500 to-teal-600", "from-violet-500 to-purple-600", "from-rose-500 to-pink-600", "from-amber-500 to-orange-600", "from-cyan-500 to-sky-600"];
    const getAvatarColor = (id) => avatarColors[(id || 0) % avatarColors.length];

    if (loading) {
        return <CoachReportingSkeleton isDarkMode={isDarkMode} />;
    }

    if (upgradeRequired) {
        return <CoachUpgradeGate featureName="Coach Reporting" />;
    }

    const students = report?.students || [];
    const quarterlyCompletion = report?.quarterlyCompletion || [];
    const recommendations = report?.suggestions || [];
    const focusAreas = report?.focusAreas || [];
    const feedbackSummary = report?.feedbackSummary || {};
    const requestSummary = report?.requestSummary || {};
    const totalQuarterlyGoals = report?.totalQuarterlyGoals || 0;

    const totalStudents = students.length;
    const totalTasks = students.reduce((sum, s) => sum + (s.totalTasks || 0), 0);
    const totalCompleted = students.reduce((sum, s) => sum + (s.completedTasks || 0), 0);
    const avgCompletion = totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;
    const totalRecommended = recommendations.length;
    const acceptedRecs = recommendations.filter(r => r.Status === "Accepted" || r.Status === "Completed").length;
    const avgResponseHours = scorecard?.avgResponseHours;
    const scorecardAcceptanceRate = scorecard?.suggestionAcceptanceRate;
    const scorecardCompletionRate = scorecard?.studentCompletionRate;

    const exportToExcel = () => {
        const wb = XLSX.utils.book_new();

        // Sheet 1: Students
        if (students.length > 0) {
            const studentRows = students.map(s => ({
                "Student": `${s.FirstName} ${s.LastName}`,
                "Total Tasks": s.totalTasks || 0,
                "Completed Tasks": s.completedTasks || 0,
                "Completion %": s.completionRate || 0,
            }));
            const ws1 = XLSX.utils.json_to_sheet(studentRows);
            XLSX.utils.book_append_sheet(wb, ws1, "Students");
        }

        // Sheet 2: Quarterly Completion
        if (quarterlyCompletion.length > 0) {
            const qRows = quarterlyCompletion.map(q => ({
                "Quarter": formatQuarterShort(q),
                "Total Tasks": q.totalTasks || 0,
                "Completed": q.completedTasks || 0,
                "Completion %": q.completionRate || 0,
            }));
            const ws2 = XLSX.utils.json_to_sheet(qRows);
            XLSX.utils.book_append_sheet(wb, ws2, "Quarterly Completion");
        }

        // Sheet 3: Focus Areas
        if (focusAreas.length > 0) {
            const faRows = focusAreas.map(fa => ({
                "Focus Area": fa.FocusAreaName,
                "Total Tasks": fa.taskCount || 0,
                "Completed": fa.completedCount || 0,
                "Completion %": fa.taskCount > 0 ? Math.round((fa.completedCount / fa.taskCount) * 100) : 0,
            }));
            const ws3 = XLSX.utils.json_to_sheet(faRows);
            XLSX.utils.book_append_sheet(wb, ws3, "Focus Areas");
        }

        // Sheet 4: Suggestions
        if (recommendations.length > 0) {
            const sugRows = recommendations.map(r => ({
                "Suggestion": r.SuggestionTitle,
                "Student": `${r.FirstName || ""} ${r.LastName || ""}`.trim(),
                "Focus Area": r.FocusAreaName || "",
                "Status": r.Status,
                "Date": r.SuggestedOn || "",
            }));
            const ws4 = XLSX.utils.json_to_sheet(sugRows);
            XLSX.utils.book_append_sheet(wb, ws4, "Suggestions");
        }

        // Sheet 5: Feedback
        const feedbackToExport = filterFeedbackByQuarter(allFeedback);
        if (feedbackToExport.length > 0) {
            const fbRows = feedbackToExport.map(f => ({
                "Student": `${f.FirstName || ""} ${f.LastName || ""}`.trim(),
                "Rating": f.Rating || "",
                "Task": f.TaskTitle || "",
                "Feedback": f.FeedbackText,
                "Date": f.CreatedOn || "",
            }));
            const ws5 = XLSX.utils.json_to_sheet(fbRows);
            XLSX.utils.book_append_sheet(wb, ws5, "Feedback");
        }

        // Sheet 6: Summary
        const summaryRows = [
            { "Metric": "Total Students", "Value": totalStudents },
            { "Metric": "Avg Completion %", "Value": avgCompletion },
            { "Metric": "Total Feedback Given", "Value": feedbackSummary.totalFeedback || 0 },
            { "Metric": "Avg Rating", "Value": feedbackSummary.avgRating || "N/A" },
            { "Metric": "Total Suggestions Sent", "Value": totalRecommended },
            { "Metric": "Suggestions Accepted", "Value": acceptedRecs },
            { "Metric": "Accept Rate %", "Value": totalRecommended > 0 ? Math.round((acceptedRecs / totalRecommended) * 100) : 0 },
            { "Metric": "Requests Approved", "Value": requestSummary.approved || 0 },
            { "Metric": "Requests Pending", "Value": requestSummary.pending || 0 },
            { "Metric": "Requests Declined", "Value": requestSummary.rejected || 0 },
            { "Metric": "Quarterly Goals", "Value": totalQuarterlyGoals },
        ];
        const ws6 = XLSX.utils.json_to_sheet(summaryRows);
        XLSX.utils.book_append_sheet(wb, ws6, "Summary");

        XLSX.writeFile(wb, `Coach_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const thClass = `px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-500"}`;
    const tdClass = `px-4 py-3 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`;
    const tableWrapper = `overflow-x-auto rounded-2xl border shadow-sm ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`;

    const toggleSection = (key) => setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

    // Wrapper class that smoothly animates a section body open/closed (grid-rows 0fr↔1fr trick).
    const gridWrapClass = (open) =>
        `grid transition-all duration-300 ease-in-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`;

    // Collapsible section header — click to expand/collapse the table below it.
    const sectionHeader = (sectionKey, Icon, label, extraClass = "mb-3") => {
        const open = expandedSections[sectionKey];
        return (
            <button
                type="button"
                onClick={() => toggleSection(sectionKey)}
                aria-expanded={open}
                className={`${extraClass} flex items-center gap-2 text-sm font-bold transition-colors ${isDarkMode ? "text-slate-200 hover:text-white" : "text-slate-700 hover:text-slate-900"}`}
            >
                <Icon size={15} />
                {label}
                <ChevronRight size={15} className={`opacity-70 transition-transform duration-300 ${open ? "rotate-90" : ""}`} />
            </button>
        );
    };

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="rounded-2xl overflow-hidden mb-8 shadow-sm">
                    <div className={`relative px-6 py-8 ${isDarkMode ? "bg-gradient-to-r from-orange-900 via-amber-900 to-yellow-900" : "bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600"}`}>
                        <Sparkles className="absolute top-4 right-6 text-white/20" size={40} />
                        <div className="relative flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white shadow-lg border border-white/30">
                                    <BarChart3 size={28} />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-extrabold tracking-tight text-white">Coach Reporting</h1>
                                    <p className="text-sm text-white/70">In-depth analytics across all your coaching activity</p>
                                </div>
                            </div>
                            <button
                                onClick={exportToExcel}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl font-semibold text-sm hover:bg-white/30 transition-colors border border-white/30 shadow-sm"
                            >
                                <Download size={16} />
                                Export Excel
                            </button>
                        </div>
                    </div>
                </div>

                {message && (
                    <div role="alert" className={`mb-5 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium ${
                        message.type === "error" ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                        : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                    }`}>{message.text}</div>
                )}

                {/* ── Summary Overview Table ── */}
                <div className="mb-8">
                    {sectionHeader("overview", BarChart3, "Overview")}
                    <div className={gridWrapClass(expandedSections.overview)}>
                        <div className="overflow-hidden">
                    <div className={tableWrapper}>
                        <table className="w-full">
                            <thead className={isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}>
                                <tr>
                                    <th className={thClass}>Metric</th>
                                    <th className={thClass}>Value</th>
                                    <th className={thClass}>Metric</th>
                                    <th className={thClass}>Value</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? "divide-slate-700/50" : "divide-slate-100"}`}>
                                <tr className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                    <td className={`${tdClass} font-medium`}><Users size={13} className="inline mr-1.5 -mt-0.5" />Students</td>
                                    <td className={`${tdClass} font-bold`}>{totalStudents}</td>
                                    <td className={`${tdClass} font-medium`}><PieChart size={13} className="inline mr-1.5 -mt-0.5" />Avg Completion</td>
                                    <td className={`${tdClass} font-bold ${avgCompletion >= 70 ? "text-primary-500" : avgCompletion >= 40 ? "text-amber-500" : "text-rose-500"}`}>{avgCompletion}%</td>
                                </tr>
                                <tr className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                    <td className={`${tdClass} font-medium`}><MessageSquare size={13} className="inline mr-1.5 -mt-0.5" />Feedback Given</td>
                                    <td className={`${tdClass} font-bold`}>{feedbackSummary.totalFeedback || 0}</td>
                                    <td className={`${tdClass} font-medium`}><Star size={13} className="inline mr-1.5 -mt-0.5" />Avg Rating</td>
                                    <td className={`${tdClass} font-bold`}>{feedbackSummary.avgRating > 0 ? `${feedbackSummary.avgRating} ★` : "—"}</td>
                                </tr>
                                <tr className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                    <td className={`${tdClass} font-medium`}><Send size={13} className="inline mr-1.5 -mt-0.5" />Suggestions Sent</td>
                                    <td className={`${tdClass} font-bold`}>{totalRecommended}</td>
                                    <td className={`${tdClass} font-medium`}><Send size={13} className="inline mr-1.5 -mt-0.5" />Accepted</td>
                                    <td className={`${tdClass} font-bold`}>{acceptedRecs} ({totalRecommended > 0 ? Math.round((acceptedRecs / totalRecommended) * 100) : 0}%)</td>
                                </tr>
                                <tr className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                    <td className={`${tdClass} font-medium`}><UserCheck size={13} className="inline mr-1.5 -mt-0.5" />Requests Approved</td>
                                    <td className={`${tdClass} font-bold text-primary-500`}>{requestSummary.approved || 0}</td>
                                    <td className={`${tdClass} font-medium`}><UserCheck size={13} className="inline mr-1.5 -mt-0.5" />Pending / Declined</td>
                                    <td className={tdClass}><span className="font-bold text-amber-500">{requestSummary.pending || 0}</span> / <span className="font-bold text-rose-500">{requestSummary.rejected || 0}</span></td>
                                </tr>
                                <tr className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                    <td className={`${tdClass} font-medium`}><Target size={13} className="inline mr-1.5 -mt-0.5" />Quarterly Goals</td>
                                    <td className={`${tdClass} font-bold`}>{totalQuarterlyGoals}</td>
                                    <td className={`${tdClass} font-medium`}>Avg Response Time</td>
                                    <td className={`${tdClass} font-bold`}>{avgResponseHours != null ? `${avgResponseHours}h` : "—"}</td>
                                </tr>
                                <tr className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                    <td className={`${tdClass} font-medium`}>Scorecard Acceptance Rate</td>
                                    <td className={`${tdClass} font-bold`}>{scorecardAcceptanceRate != null ? `${scorecardAcceptanceRate}%` : "—"}</td>
                                    <td className={`${tdClass} font-medium`}>Scorecard Completion Rate</td>
                                    <td className={`${tdClass} font-bold`}>{scorecardCompletionRate != null ? `${scorecardCompletionRate}%` : "—"}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                        </div>
                    </div>
                </div>

                {/* ── Students Table ── */}
                <div className="mb-8">
                    {sectionHeader("students", Users, "Student Completion")}
                    <div className={gridWrapClass(expandedSections.students)}>
                        <div className="overflow-hidden">
                    {students.length === 0 ? (
                        <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No students yet.</p>
                    ) : (
                        <div className={tableWrapper}>
                            <table className="w-full">
                                <thead className={isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}>
                                    <tr>
                                        <th className={thClass}>Student</th>
                                        <th className={thClass}>Total Tasks</th>
                                        <th className={thClass}>Completed</th>
                                        <th className={thClass}>Completion</th>
                                        <th className={`${thClass} w-48`}>Progress</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? "divide-slate-700/50" : "divide-slate-100"}`}>
                                    {students.map(s => (
                                        <tr key={s.ClientID} className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                            <td className={tdClass}>
                                                <div className="flex items-center gap-2.5">
                                                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getAvatarColor(s.ClientID)} flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0`}>
                                                        {s.FirstName?.[0]}{s.LastName?.[0]}
                                                    </div>
                                                    <span className={`font-semibold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                                                        {s.FirstName} {s.LastName}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className={tdClass}>{s.totalTasks || 0}</td>
                                            <td className={tdClass}>{s.completedTasks || 0}</td>
                                            <td className={tdClass}>
                                                <span className={`font-bold ${s.completionRate >= 70 ? "text-primary-500" : s.completionRate >= 40 ? "text-amber-500" : "text-rose-500"}`}>
                                                    {s.completionRate}%
                                                </span>
                                            </td>
                                            <td className={tdClass}>
                                                <div className={`w-full h-2 rounded-full ${isDarkMode ? "bg-slate-700" : "bg-slate-200"}`}>
                                                    <div className={`h-2 rounded-full transition-all ${s.completionRate >= 70 ? "bg-primary-500" : s.completionRate >= 40 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${s.completionRate}%` }} />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                        </div>
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-2 mb-8">
                    {/* ── Quarterly Completion Table ── */}
                    {quarterlyCompletion.length > 0 && (
                        <div>
                            {sectionHeader("quarterly", TrendingUp, "Quarterly Completion")}
                            <div className={gridWrapClass(expandedSections.quarterly)}>
                                <div className="overflow-hidden">
                            <div className={tableWrapper}>
                                <table className="w-full">
                                    <thead className={isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}>
                                        <tr>
                                            <th className={thClass}>Quarter</th>
                                            <th className={thClass}>Tasks</th>
                                            <th className={thClass}>Done</th>
                                            <th className={thClass}>Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? "divide-slate-700/50" : "divide-slate-100"}`}>
                                        {quarterlyCompletion.map(q => (
                                            <tr key={q.QuarterID} className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                                <td className={`${tdClass} font-medium`}>{formatQuarterShort(q)}</td>
                                                <td className={tdClass}>{q.totalTasks || 0}</td>
                                                <td className={tdClass}>{q.completedTasks || 0}</td>
                                                <td className={tdClass}>
                                                    <span className={`font-bold ${q.completionRate >= 70 ? "text-primary-500" : q.completionRate >= 40 ? "text-amber-500" : "text-rose-500"}`}>
                                                        {q.completionRate}%
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Focus Area Table ── */}
                    {focusAreas.length > 0 && (
                        <div>
                            <h2 className={`text-sm font-bold mb-3 flex items-center gap-2 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                                <Flag size={15} />Focus Area Breakdown
                            </h2>
                            <div className={tableWrapper}>
                                <table className="w-full">
                                    <thead className={isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}>
                                        <tr>
                                            <th className={thClass}>Focus Area</th>
                                            <th className={thClass}>Tasks</th>
                                            <th className={thClass}>Done</th>
                                            <th className={thClass}>Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? "divide-slate-700/50" : "divide-slate-100"}`}>
                                        {focusAreas.map(fa => {
                                            const rate = fa.taskCount > 0 ? Math.round((fa.completedCount / fa.taskCount) * 100) : 0;
                                            return (
                                                <tr key={fa.FocusAreaName} className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                                    <td className={`${tdClass} font-medium`}>{fa.FocusAreaName}</td>
                                                    <td className={tdClass}>{fa.taskCount || 0}</td>
                                                    <td className={tdClass}>{fa.completedCount || 0}</td>
                                                    <td className={tdClass}>
                                                        <span className={`font-bold ${rate >= 70 ? "text-primary-500" : rate >= 40 ? "text-amber-500" : "text-rose-500"}`}>
                                                            {rate}%
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Suggestions Table ── */}
                {recommendations.length > 0 && (
                    <div className="mb-8">
                        <h2 className={`text-sm font-bold mb-3 flex items-center gap-2 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                            <Sparkles size={15} />Suggestions
                        </h2>
                        <div className={tableWrapper}>
                            <table className="w-full">
                                <thead className={isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}>
                                    <tr>
                                        <th className={thClass}>Suggestion</th>
                                        <th className={thClass}>Student</th>
                                        <th className={thClass}>Focus Area</th>
                                        <th className={thClass}>Date</th>
                                        <th className={thClass}>Status</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? "divide-slate-700/50" : "divide-slate-100"}`}>
                                    {recommendations.map(r => (
                                        <tr key={r.SuggestionID} className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                            <td className={`${tdClass} font-medium max-w-xs truncate`}>{r.SuggestionTitle}</td>
                                            <td className={tdClass}>{r.FirstName} {r.LastName}</td>
                                            <td className={tdClass}>{r.FocusAreaName || "—"}</td>
                                            <td className={`${tdClass} text-xs`}>{r.SuggestedOn || "—"}</td>
                                            <td className={tdClass}>
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                                    r.Status === "Completed" || r.Status === "Accepted" ? "bg-primary-100 text-primary-700" :
                                                    r.Status === "In Progress" ? "bg-blue-100 text-blue-700" :
                                                    r.Status === "Pending" ? "bg-amber-100 text-amber-700" :
                                                    "bg-slate-100 text-slate-600"
                                                }`}>{r.Status}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ── Feedback History Table ── */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                        {sectionHeader("feedback", MessageSquare, "Feedback History", "")}
                        <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl ${isDarkMode ? "bg-slate-800/70 border border-slate-700/60" : "bg-white border border-slate-200"}`}>
                            <Filter size={13} className={isDarkMode ? "text-slate-400" : "text-slate-500"} />
                            <select value={selectedQuarter} onChange={(e) => setSelectedQuarter(e.target.value)}
                                className={`select-styled text-xs rounded-lg px-2 py-1.5 border font-medium shadow-sm ${isDarkMode ? "bg-slate-800 border-slate-600 text-slate-200 hover:border-slate-500" : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"}`}>
                                <option value="">All Time</option>
                                {quarters.map((q) => <option key={q.QuarterID} value={q.QuarterID}>{formatQuarterLabel(q)}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className={gridWrapClass(expandedSections.feedback)}>
                        <div className="overflow-hidden">
                    {filterFeedbackByQuarter(allFeedback).length === 0 ? (
                        <div className={`rounded-2xl border p-10 text-center ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
                            <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${isDarkMode ? "bg-slate-700/60" : "bg-slate-100"}`}>
                                <MessageSquare className={isDarkMode ? "text-slate-500" : "text-slate-300"} size={32} />
                            </div>
                            <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                                {selectedQuarter ? "No feedback for this quarter" : "No feedback yet"}
                            </h3>
                            <p className={`text-sm max-w-sm mx-auto ${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>
                                {selectedQuarter ? "Try selecting a different quarter." : "Click a student to leave feedback and track their progress."}
                            </p>
                        </div>
                    ) : (
                        <div className={tableWrapper}>
                            <table className="w-full">
                                <thead className={isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}>
                                    <tr>
                                        <th className={thClass}>Student</th>
                                        <th className={thClass}>Rating</th>
                                        <th className={thClass}>Task</th>
                                        <th className={thClass}>Feedback</th>
                                        <th className={thClass}>Date</th>
                                        <th className={thClass}></th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? "divide-slate-700/50" : "divide-slate-100"}`}>
                                    {filterFeedbackByQuarter(allFeedback).map(f => (
                                        <tr key={f.FeedbackID} className={isDarkMode ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}>
                                            <td className={tdClass}>
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${getAvatarColor(f.ClientID)} flex items-center justify-center text-white font-bold text-[9px] flex-shrink-0`}>
                                                        {f.FirstName?.[0]}{f.LastName?.[0]}
                                                    </div>
                                                    <span className={`font-medium text-sm ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{f.FirstName} {f.LastName}</span>
                                                </div>
                                            </td>
                                            <td className={tdClass}>
                                                {f.Rating ? (
                                                    <div className="flex items-center gap-0.5">
                                                        {[1, 2, 3, 4, 5].map(s => (
                                                            <Star key={s} size={11} className={s <= f.Rating ? "text-amber-400 fill-amber-400" : isDarkMode ? "text-slate-600" : "text-slate-300"} />
                                                        ))}
                                                    </div>
                                                ) : <span className={isDarkMode ? "text-slate-500" : "text-slate-400"}>—</span>}
                                            </td>
                                            <td className={tdClass}>
                                                {f.TaskTitle ? (
                                                    <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${isDarkMode ? "bg-cyan-900/30 text-cyan-300" : "bg-cyan-50 text-cyan-700"}`}>{f.TaskTitle}</span>
                                                ) : "—"}
                                            </td>
                                            <td className={`${tdClass} max-w-xs`}>
                                                <p className="truncate">{f.FeedbackText}</p>
                                            </td>
                                            <td className={`${tdClass} text-xs whitespace-nowrap`}>{f.CreatedOn}</td>
                                            <td className={`${tdClass} text-right`}>
                                                <button onClick={() => deleteFeedback(f.FeedbackID)} disabled={actionLoading}
                                                    className="text-red-400 hover:text-red-600 rounded-lg p-1 transition-colors" title="Delete feedback">
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
